import { Link } from "react-router-dom";
import { Receipt, Stethoscope, UserCheck, TrendingUp, CalendarClock } from "lucide-react";
import StatusBadge from "./StatusBadge";
import TestDataBadge from "../common/TestDataBadge";
import DecidedBy from "../common/DecidedBy";
import DecisionNotes from "../common/DecisionNotes";
import WaitingOn from "../common/WaitingOn";
import { formatINR } from "../../lib/expenseOptions";
import { formatISTDate } from "../../lib/datetime";

export default function ExpenseCard({ expense, showOfficer = false, action = null }) {
  return (
    <div
      className="bg-white border border-callisto rounded-xl p-4"
      data-testid={`expense-card-${expense.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <Link to={`/expenses/${expense.id}`} className="min-w-0 flex-1 group">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-bold text-callisto-navy leading-tight group-hover:underline">
              {formatINR(expense.amount)}
            </span>
            <span className="text-[11px] font-semibold rounded-full bg-callisto-navy/5 text-callisto-navy px-2 py-0.5">
              {expense.expense_type}
            </span>
            {/* The two stamped routing causes, kept visually apart even
                though the route is the same (D-007): over the threshold vs
                outside the filing window. `amount_escalated`, never
                `escalated` — that name is the rework counter. */}
            {expense.amount_escalated === true && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-bold rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5"
                data-testid={`expense-escalated-${expense.id}`}
              >
                <TrendingUp size={10} />
                Escalated
              </span>
            )}
            {expense.late_filed === true && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-bold rounded-full bg-purple-50 text-purple-800 border border-purple-200 px-1.5 py-0.5"
                data-testid={`expense-late-${expense.id}`}
              >
                <CalendarClock size={10} />
                Late filed
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-callisto-muted">
            {/* IST prose, not the raw ISO the API stores. */}
            {formatISTDate(expense.expense_date)}
            {showOfficer && expense.sales_officer_name && (
              <>
                <span className="mx-1.5">·</span>
                <span className="inline-flex items-center gap-1">
                  <UserCheck size={11} />
                  <span className="font-semibold text-callisto-navy">
                    {expense.sales_officer_name}
                  </span>
                </span>
              </>
            )}
          </p>
          <p className="mt-1.5 text-xs text-callisto-body line-clamp-2">{expense.description}</p>
          {expense.linked_doctor_name && (
            <p className="mt-1 text-[11px] text-callisto-muted flex items-center gap-1">
              <Stethoscope size={11} />
              <span>linked: {expense.linked_doctor_name}</span>
            </p>
          )}
          {expense.receipt_note && (
            <p className="mt-1 text-[11px] text-callisto-muted flex items-center gap-1">
              <Receipt size={11} />
              <span>{expense.receipt_note}</span>
            </p>
          )}
        </Link>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex flex-col items-end gap-1">
            <StatusBadge status={expense.status} waitingOn={expense.waiting_on} />
            <TestDataBadge record={expense} className="ml-1" />
            <DecidedBy record={expense} className="text-right" />
          </div>
          {action}
        </div>
      </div>
      {/* IN-FLIGHT STATUS, below the row rather than beside the badge: the line
          is a sentence, and squeezing it into the right-hand column would
          truncate the approver's name — the one part worth reading. Renders
          nothing on decided claims. */}
      <WaitingOn record={expense} className="mt-2.5" />
      {/* The WHY, on the card (F344's gap): a rejection remark or the rework
          reason, not only on the detail page. */}
      <DecisionNotes record={expense} className="mt-2" />
    </div>
  );
}
